import JSZip from 'jszip';

// A worksheet name longer than this, or containing any of these characters, makes Excel reject the
// whole workbook as corrupt rather than reporting the offending sheet.
const maxSheetNameLength = 31;
const invalidSheetNameCharacters = /[:\\/?*[\]]/g;

const alphabetLength = 26;
const firstLetterCharCode = 65;

// XML 1.0 forbids these code points outright, so a stray control character in engine output would
// make the file unreadable.
const forbiddenXmlCharacters = /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g;

const xlsxMimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const contentTypesNamespace = 'http://schemas.openxmlformats.org/package/2006/content-types';
const relationshipsNamespace = 'http://schemas.openxmlformats.org/package/2006/relationships';
const officeDocumentRelationship = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument';
const worksheetRelationship = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet';
const spreadsheetNamespace = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const documentRelationshipsNamespace = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const workbookContentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml';
const worksheetContentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml';

const xmlDeclaration = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';

function escapeXml(value) {
    return value
        .replace(forbiddenXmlCharacters, '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

function toCellText(value) {
    if (value == null) {
        return '';
    }

    return `${value}`;
}

function columnReference(columnIndex) {
    let reference = '';
    let remaining = columnIndex;

    while (remaining >= 0) {
        const letter = String.fromCharCode(firstLetterCharCode + (remaining % alphabetLength));
        reference = `${letter}${reference}`;
        remaining = Math.floor(remaining / alphabetLength) - 1;
    }

    return reference;
}

function safeSheetName(name, sheetIndex) {
    const cleaned = toCellText(name).replace(invalidSheetNameCharacters, ' ').trim();

    if (cleaned === '') {
        return `Sheet${sheetIndex + 1}`;
    }

    return cleaned.slice(0, maxSheetNameLength);
}

// Excel also rejects a workbook with two sheets of the same name, compared ignoring case.
function uniqueSheetNames(names) {
    const usedNames = new Set();

    return names.map((name) => {
        let candidate = name;
        let copyNumber = 2;

        while (usedNames.has(candidate.toLowerCase())) {
            const suffix = ` (${copyNumber})`;
            candidate = `${name.slice(0, maxSheetNameLength - suffix.length).trimEnd()}${suffix}`;
            copyNumber += 1;
        }

        usedNames.add(candidate.toLowerCase());

        return candidate;
    });
}

// Every value is written as an inline string, so the workbook needs no shared-strings part and no
// number formats. Engine output is diagnostic text that must read back exactly as it was shown.
function buildCell(value, columnIndex, rowNumber) {
    const reference = `${columnReference(columnIndex)}${rowNumber}`;
    const text = escapeXml(toCellText(value));
    return `<c r="${reference}" t="inlineStr"><is><t xml:space="preserve">${text}</t></is></c>`;
}

function buildRow(cells, rowNumber) {
    const renderedCells = cells
        .map((cell, columnIndex) => buildCell(cell, columnIndex, rowNumber))
        .join('');
    return `<row r="${rowNumber}">${renderedCells}</row>`;
}

function buildSheetXml(sheet) {
    const rows = [sheet.headers, ...sheet.rows]
        .map((cells, rowIndex) => buildRow(cells, rowIndex + 1))
        .join('');

    return `${xmlDeclaration}<worksheet xmlns="${spreadsheetNamespace}"><sheetData>${rows}</sheetData></worksheet>`;
}

function buildContentTypesXml(sheetCount) {
    const relationshipDefault = '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>';
    const xmlDefault = '<Default Extension="xml" ContentType="application/xml"/>';
    const workbookOverride = `<Override PartName="/xl/workbook.xml" ContentType="${workbookContentType}"/>`;
    const sheetOverrides = Array.from({ length: sheetCount }, (ignored, index) =>
        `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="${worksheetContentType}"/>`).join('');

    return `${xmlDeclaration}<Types xmlns="${contentTypesNamespace}">${relationshipDefault}${xmlDefault}${workbookOverride}${sheetOverrides}</Types>`;
}

function buildRootRelationshipsXml() {
    const relationship = `<Relationship Id="rId1" Type="${officeDocumentRelationship}" Target="xl/workbook.xml"/>`;

    return `${xmlDeclaration}<Relationships xmlns="${relationshipsNamespace}">${relationship}</Relationships>`;
}

function buildWorkbookXml(sheetNames) {
    const sheets = sheetNames.map((name, index) =>
        `<sheet name="${escapeXml(name)}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`).join('');

    return `${xmlDeclaration}<workbook xmlns="${spreadsheetNamespace}" xmlns:r="${documentRelationshipsNamespace}"><sheets>${sheets}</sheets></workbook>`;
}

function buildWorkbookRelationshipsXml(sheetCount) {
    const relationships = Array.from({ length: sheetCount }, (ignored, index) =>
        `<Relationship Id="rId${index + 1}" Type="${worksheetRelationship}" Target="worksheets/sheet${index + 1}.xml"/>`).join('');

    return `${xmlDeclaration}<Relationships xmlns="${relationshipsNamespace}">${relationships}</Relationships>`;
}

/**
 * Builds an Excel workbook from a list of { name, headers, rows } sets, one worksheet per set, and
 * resolves the .xlsx file as a Blob. Cells are written as inline strings and no cells are merged.
 */
export default function buildXlsxWorkbook(sheets) {
    const zip = new JSZip();
    const sheetNames = uniqueSheetNames(sheets.map((sheet, index) => safeSheetName(sheet.name, index)));

    zip.file('[Content_Types].xml', buildContentTypesXml(sheets.length));
    zip.folder('_rels').file('.rels', buildRootRelationshipsXml());
    zip.folder('xl').file('workbook.xml', buildWorkbookXml(sheetNames));
    zip.folder('xl').folder('_rels').file('workbook.xml.rels', buildWorkbookRelationshipsXml(sheets.length));

    sheets.forEach((sheet, index) => {
        zip.folder('xl').folder('worksheets').file(`sheet${index + 1}.xml`, buildSheetXml(sheet));
    });

    return zip.generateAsync({ type: 'blob', mimeType: xlsxMimeType, compression: 'DEFLATE' });
}
